import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { publicApi } from "@/admin/api";
import { usePageContent } from "@/context/PageContentContext";
import { contact } from "@/data/contact";
import Seo from "@/components/site/Seo";
import { Img } from "@/assets";

const CATEGORIES = ["Sales enquiry", "Corporate Relationship", "Media", "Careers", "General enquiry"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [params] = useSearchParams();
  const initialCat = CATEGORIES.includes(params.get("category")) ? params.get("category") : "Sales enquiry";
  const content = usePageContent();

  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    category: initialCat, message: "", consent: false, website: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState("idle");
  const [errBanner, setErrBanner] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = (values) => {
    const e = {};
    if (values.name.trim().length < 2 || values.name.length > 120) e.name = "Please enter your name (2–120 characters).";
    if (values.company && values.company.length > 160) e.company = "Company name is too long.";
    if (!values.email || !EMAIL_RE.test(values.email) || values.email.length > 200) e.email = "Please enter a valid email.";
    if (values.phone && !/^[0-9+\-\s()]{0,40}$/.test(values.phone)) e.phone = "Phone contains invalid characters.";
    if (!CATEGORIES.includes(values.category)) e.category = "Please choose a category.";
    if (values.message.trim().length < 10 || values.message.length > 4000)
      e.message = "Please enter a message between 10 and 4000 characters.";
    if (!values.consent) e.consent = "Please confirm consent to proceed.";
    return e;
  };

  useEffect(() => { setErrors(validate(form)); }, [form]);
  const handleBlur = (k) => setTouched((t) => ({ ...t, [k]: true }));
  const showErr = (k) => touched[k] && errors[k];

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setErrBanner("");
    setTouched({ name: true, email: true, category: true, message: true, consent: true });
    const eNow = validate(form);
    setErrors(eNow);
    if (Object.keys(eNow).length > 0) return;
    setStatus("submitting");
    try {
      await publicApi.submitEnquiry({
        name: form.name.trim(), company: form.company.trim() || null,
        email: form.email.trim(), phone: form.phone.trim() || null,
        category: form.category, message: form.message.trim(),
        consent: true, website: form.website,
      });
      setStatus("success");
    } catch (e) {
      setStatus("error");
      setErrBanner(`We couldn't submit your enquiry. Please try again in a moment, or email ${contact.general_email.address}.`);
    }
  };

  return (
    <>
      <Seo />
      <section className="gp-page-hero" data-testid="contact-hero" style={{ paddingBottom: 0, minHeight: "35vh" }}>
        <div className="gp-page-hero__img" aria-hidden="true"><Img id="port-aerial-dawn" alt="" /></div>
        <div className="gp-wrap gp-page-hero__inner" style={{ paddingBottom: "clamp(48px, 6vw, 80px)" }}>
          <div className="gp-eyebrow">
            <span className="gp-eyebrow-num">06</span><span className="gp-eyebrow-sep" /> Contact
          </div>
          <h1 className="gp-h1 gp-page-hero__title">Contact Gold Petroleum.</h1>
          <p className="gp-page-hero__lede">
            Reach the team by email or phone, or send a written enquiry using the form below.
            We respond directly.
          </p>
        </div>
      </section>

      <section className="gp-section gp-on-navy-2" data-testid="contact-tiles">
        <div className="gp-wrap">
          <div className="gp-eyebrow">
            <span className="gp-eyebrow-num">01</span><span className="gp-eyebrow-sep" /> Direct lines
          </div>
          <h2 className="gp-h2" style={{ color: "var(--gp-white)", maxWidth: "22ch", marginBottom: 56 }}>
            Reach the people who make the decisions.
          </h2>

          <div className="gp-contact-grid">
            <div className="gp-contact-tile" data-testid="contact-tile-company-phone">
              <div className="gp-contact-tile__label">Telephone</div>
              <div className="gp-contact-tile__name" style={{ color: "var(--gp-white)" }}>Company telephone</div>
              <div className="gp-contact-tile__role">All hours enquiries</div>
              <div className="gp-contact-tile__lines">
                <a href={`tel:${contact.company_phone.tel}`} data-testid="contact-company-tel">
                  {contact.company_phone.raw}
                </a>
              </div>
            </div>

            <div className="gp-contact-tile" data-testid="contact-tile-emails">
              <div className="gp-contact-tile__label">Email</div>
              <div className="gp-contact-tile__name" style={{ color: "var(--gp-white)" }}>Written enquiries</div>
              <div className="gp-contact-tile__role">Sales &amp; general mailboxes</div>
              <div className="gp-contact-tile__lines">
                <a href={`mailto:${contact.sales_email.address}`} data-testid="contact-email-sales">
                  Sales · {contact.sales_email.address}
                </a>
                <a href={`mailto:${contact.general_email.address}`} data-testid="contact-email-general">
                  General · {contact.general_email.address}
                </a>
              </div>
            </div>

            <div className="gp-contact-tile" data-testid="contact-tile-ken">
              <div className="gp-contact-tile__label">Leadership</div>
              <div className="gp-contact-tile__name" style={{ color: "var(--gp-white)" }}>{contact.leadership.name}</div>
              <div className="gp-contact-tile__role">{contact.leadership.title}</div>
              <div className="gp-contact-tile__lines">
                <span style={{ color: "rgba(255,255,255,0.6)" }}>Reach via company telephone or the sales mailbox above.</span>
              </div>
            </div>

            <div className="gp-contact-tile" data-testid="contact-tile-fabio">
              <div className="gp-contact-tile__label">Contact</div>
              <div className="gp-contact-tile__name" style={{ color: "var(--gp-white)" }}>Contact {contact.sales_contact.name}</div>
              <div className="gp-contact-tile__role">{contact.sales_contact.role}</div>
              <div className="gp-contact-tile__lines">
                <a href={`tel:${contact.sales_contact.tel}`} data-testid="contact-fabio-ca">
                  {contact.sales_contact.region}: {contact.sales_contact.raw}
                </a>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 64, paddingTop: 32, borderTop: "1px solid var(--gp-hairline-dark)" }}>
            <div className="gp-eyebrow" style={{ marginBottom: 12 }}>
              <span className="gp-eyebrow-sep" /> Location
            </div>
            <div style={{ color: "var(--gp-white)", fontFamily: "var(--gp-font-display)", fontSize: "1.25rem" }}>
              United Arab Emirates
            </div>
          </div>
        </div>
      </section>

      <section className="gp-section gp-on-white" data-testid="contact-form-section">
        <div className="gp-wrap gp-narrow">
          <div className="gp-eyebrow" style={{ color: "var(--gp-ink)" }}>
            <span className="gp-eyebrow-num">02</span><span className="gp-eyebrow-sep" /> Enquiry form
          </div>
          <h2 className="gp-h2" style={{ maxWidth: "24ch", marginBottom: 12 }}>Send us a written enquiry.</h2>
          <p className="gp-body" style={{ marginBottom: 40, color: "var(--gp-mute)" }}>
            We treat every enquiry confidentially. A member of the Gold Petroleum team will review your message and be in touch.
          </p>

          {status === "success" ? (
            <div className="gp-form-success" data-testid="form-success">
              <div className="gp-eyebrow" style={{ color: "var(--gp-ink)" }}>
                <span className="gp-eyebrow-sep" /> Submitted
              </div>
              <h3 className="gp-h3" style={{ marginBottom: 12 }}>Your enquiry has been submitted.</h3>
              <p className="gp-body">A member of the Gold Petroleum team will review it.</p>
            </div>
          ) : (
            <form className="gp-form" onSubmit={onSubmit} noValidate data-testid="contact-form">
              {errBanner && <div className="gp-form-error" role="alert" data-testid="form-error-banner">{errBanner}</div>}
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
                <label>Website (leave blank)
                  <input type="text" tabIndex={-1} autoComplete="off" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="honeypot" />
                </label>
              </div>

              <div className="gp-form-row">
                <label className="gp-input">
                  <span>Name *</span>
                  <input type="text" required maxLength={120} value={form.name} onChange={(e) => set("name", e.target.value)} onBlur={() => handleBlur("name")} aria-invalid={!!showErr("name")} data-testid="form-name" />
                  {showErr("name") && <em>{errors.name}</em>}
                </label>
                <label className="gp-input">
                  <span>Company</span>
                  <input type="text" maxLength={160} value={form.company} onChange={(e) => set("company", e.target.value)} onBlur={() => handleBlur("company")} data-testid="form-company" />
                </label>
              </div>

              <div className="gp-form-row">
                <label className="gp-input">
                  <span>Email *</span>
                  <input type="email" required maxLength={200} value={form.email} onChange={(e) => set("email", e.target.value)} onBlur={() => handleBlur("email")} aria-invalid={!!showErr("email")} data-testid="form-email" />
                  {showErr("email") && <em>{errors.email}</em>}
                </label>
                <label className="gp-input">
                  <span>Phone</span>
                  <input type="tel" maxLength={40} value={form.phone} onChange={(e) => set("phone", e.target.value)} onBlur={() => handleBlur("phone")} data-testid="form-phone" />
                </label>
              </div>

              <label className="gp-input">
                <span>Category *</span>
                <select value={form.category} onChange={(e) => set("category", e.target.value)} onBlur={() => handleBlur("category")} data-testid="form-category">
                  {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </label>

              <label className="gp-input">
                <span>Message *</span>
                <textarea rows={6} required minLength={10} maxLength={4000} value={form.message} onChange={(e) => set("message", e.target.value)} onBlur={() => handleBlur("message")} aria-invalid={!!showErr("message")} data-testid="form-message" />
                {showErr("message") && <em>{errors.message}</em>}
              </label>

              <label className="gp-check" data-testid="form-consent-label">
                <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} onBlur={() => handleBlur("consent")} data-testid="form-consent" />
                <span>I have read the privacy notice and consent to Gold Petroleum FZ LLC contacting me about this enquiry.</span>
              </label>
              {showErr("consent") && <em className="gp-input-error">{errors.consent}</em>}

              <div style={{ marginTop: 8 }}>
                <button type="submit" className="gp-btn gp-btn--on-white" disabled={status === "submitting"} data-testid="form-submit">
                  {status === "submitting" ? "Submitting…" : "Submit enquiry"}
                  <span className="gp-arrow" aria-hidden="true">→</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
